import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, DropButton, Text, TextInput } from 'grommet';
import { FormClose, Clear, FormSearch } from 'grommet-icons';
import { authorizedFetch } from '../../utils';
import TagSelector from '../tag-selector';
import Loader from '../../components/loader';
import AddableEventSelect from '../../components/addable-event-select';
import Meet, { MeetDescription } from './meet';
import {
  COMPARISON_OPERATORS,
  CONDITION_TYPES,
  ATTR_PROPERTY_NAMES,
  EXISTS,
  getNewCondition
} from './filter-utils';
import {
  getMapping as getQueryParams,
  getFlattenedEventMapping
} from '../../reporting-utils';
import { searchItemsByType } from '../../items/items.actions';
import { USER_ATTRIBUTE, WEB_API_INTEGRATION } from '../../items/item-types';
import { FTSelect } from '../inputs';
import { addError } from '../../toasts/toasts.actions.js';
import { flatMap } from 'lodash';

export const PROFILE_FIELDS = [
  {
    displayName: 'Age',
    cdsField: 'properties.age',
    field: 'profile.age'
  },
  {
    displayName: 'Email',
    cdsField: 'properties.email',
    isKeyword: true,
    field: 'profile.email'
  },
  {
    displayName: 'Gender',
    cdsField: 'properties.gender',
    isKeyword: true,
    field: 'profile.gender'
  },
  {
    displayName: 'State',
    cdsField: 'properties.state',
    isKeyword: true,
    field: 'profile.state'
  },
  {
    displayName: 'Is Phone Wireless',
    cdsField: 'properties.phoneIsWireless',
    field: 'profile.phoneIsWireless'
  }
];

export const AVAILABLE_DEVICE_FIELDS = [
  {
    label: 'Device Type',
    field: 'deviceName'
  },
  {
    label: 'Device Brand',
    field: 'brandName'
  },
  {
    label: 'Device Model',
    field: 'deviceModel'
  }
];

export const INFERRED_ATTRIBUTE_VALUES = [
  {
    label: 'Median Age',
    field: 'profile.inferredAttrs.medianAge'
  },
  {
    label: 'Median Household Income',
    field: 'profile.inferredAttrs.householdIncome'
  },
  {
    label: 'Median Home Value',
    field: 'profile.inferredAttrs.homeValue'
  },
  {
    label: 'Total Population',
    field: 'profile.inferredAttrs.totalPopulation'
  }
];

const CONDITION_TYPES_USING_TERMS = [
  CONDITION_TYPES.PROFILE,
  CONDITION_TYPES.DEVICE,
  CONDITION_TYPES.QUERY_PARAM,
  CONDITION_TYPES.PARTNER,
  CONDITION_TYPES.INFERRED_ATTRIBUTES
];

const fieldAbortControllers = {};

const getUniqueTerms = async (
  conditionType,
  field,
  setUniqueTerms,
  onError,
  filterText = '',
  exclude = []
) => {
  if (field === 'properties.attrs.LikePurc') {
    const booleanTerms = ['true', 'false'];
    if (filterText) {
      setUniqueTerms(booleanTerms.filter(t => t.indexOf(filterText) !== -1));
    } else {
      setUniqueTerms(booleanTerms);
    }
    return;
  }

  // Profile/partner attributes were sourced from the decommissioned Unomi
  // context-profile index. No replacement value-lookup endpoint exists, so
  // values are entered free-form without suggestions.
  if (
    conditionType === CONDITION_TYPES.PROFILE ||
    conditionType === CONDITION_TYPES.PARTNER
  ) {
    setUniqueTerms([]);
    return;
  }

  let aggs;

  if (fieldAbortControllers[field]) {
    fieldAbortControllers[field].abort();
  }
  fieldAbortControllers[field] = new AbortController();

  let fieldToQueryWith = field;

  if (conditionType === CONDITION_TYPES.DEVICE) {
    fieldToQueryWith += '.keyword';
  } else {
    const mapping = await getFlattenedEventMapping();
    const isTextField = mapping[field] === 'text';

    if (isTextField) {
      fieldToQueryWith += '.keyword';
    }
  }

  aggs = {
    fields: {
      terms: { field: fieldToQueryWith, exclude: ['', ...exclude] }
    }
  };

  const query = {
    aggs,
    size: 0
  };

  if (filterText) {
    query.query = {
      wildcard: {
        [field]: `${filterText}*`
      }
    };
  }

  authorizedFetch(
    conditionType === CONDITION_TYPES.DEVICE
      ? `/api/search/devices/_search`
      : `/api/cds-search/reporting-event-*/_search`,
    'POST',
    query,
    fieldAbortControllers[field].signal
  )
    .then(response => {
      setUniqueTerms(response.aggregations.fields.buckets.map(b => b.key));
    })
    .catch(ex => {
      if (ex.name !== 'AbortError') {
        onError('Unable to get terms', JSON.stringify(ex));
      }
    });
};

const QueryFieldInput = ({
  availableQueryParams,
  value,
  onChange,
  canEdit
}) => {
  return (
    <AddableEventSelect
      fieldBase='properties.url.queryParams'
      options={availableQueryParams}
      value={value}
      onChange={newValue => {
        onChange({ propertyName: newValue });
      }}
      canEdit={canEdit}
    />
  );
};

const PartnerFieldSelect = ({
  availablePartnerProperties,
  value,
  onChange,
  canEdit
}) => {
  return (
    <AddableEventSelect
      fieldBase='profile.partnerAttrs'
      options={availablePartnerProperties}
      value={value}
      onChange={newValue => {
        onChange({ propertyName: newValue });
      }}
      canEdit={canEdit}
    />
  );
};

const DeviceFieldSelect = ({ value, onChange, canEdit }) => {
  return (
    <FTSelect
      size='small'
      options={AVAILABLE_DEVICE_FIELDS}
      labelKey='label'
      valueKey='field'
      disabled={!canEdit}
      value={AVAILABLE_DEVICE_FIELDS.find(c => c.field === value)}
      onChange={optionValue => {
        onChange({ propertyName: optionValue.field });
      }}
    />
  );
};

const buildProfilePropOptions = attrs => {
  const showMore = {
    displayName: 'Search to show more...',
    field: null
  };

  if (attrs.length > 15) {
    return [...attrs.slice(0, 15), showMore];
  }

  return attrs;
};

const ProfileFieldSelect = ({
  availableProfileProperties,
  value,
  onChange,
  onError,
  canEdit
}) => {
  const [filteredProfileProperties, setFilteredProfileProperties] = useState(
    buildProfilePropOptions(availableProfileProperties)
  );

  const [profilePropertiesToRender, setProfilePropetiesToRender] = useState([]);

  const matchingProperty = availableProfileProperties.find(
    a => a.field === value
  );

  useEffect(() => {
    const newPropertiesToRender = [...filteredProfileProperties];
    if (
      matchingProperty &&
      !newPropertiesToRender.find(f => f.field === matchingProperty.field)
    ) {
      if (newPropertiesToRender.length > 15) {
        newPropertiesToRender.splice(15, 0, matchingProperty);
      } else {
        newPropertiesToRender.push(matchingProperty);
      }
    }
    setProfilePropetiesToRender(newPropertiesToRender);
  }, [filteredProfileProperties, matchingProperty]);

  return (
    <FTSelect
      size='small'
      options={profilePropertiesToRender}
      labelKey='displayName'
      valueKey='field'
      value={matchingProperty}
      disabled={!canEdit}
      icon={<FormSearch />}
      handleSearch={search => {
        if (search === '') {
          setFilteredProfileProperties(
            buildProfilePropOptions(availableProfileProperties)
          );
        } else {
          setFilteredProfileProperties(
            buildProfilePropOptions(
              availableProfileProperties.filter(
                ap =>
                  ap.displayName.toLowerCase().indexOf(search) !== -1 ||
                  ap.field.toLowerCase().indexOf(search) !== -1
              )
            )
          );
        }
      }}
      onChange={optionValue => {
        const addedField = optionValue?.field;
        if (
          addedField === undefined ||
          addedField.charAt(addedField.length - 1) === '.'
        ) {
          onError('Error adding field', 'Field value is invalid.');
        } else {
          onChange({ propertyName: addedField });
        }
      }}
    />
  );
};

const SessionAttribute = ({
  sessionAttribute,
  onChange,
  onRemove,
  background,
  availableProfileProperties,
  availablePartnerProperties,
  availableQueryParams,
  availableInferredProperties,
  audienceIds,
  setAudienceIds,
  canEdit
}) => {
  const { propertyName, comparisonOperator, propertyValues, inverse } =
    sessionAttribute;
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionSearch, setSuggestionSearch] = useState('');
  const [showPropertyValue, setShowPropertyValue] = useState();
  const dispatch = useDispatch();

  const onError = (primary, secondary) =>
    dispatch(addError(primary, secondary));
  const width =
    sessionAttribute._type === CONDITION_TYPES.AUDIENCE ? '618px' : `130px`;

  useEffect(() => {
    //only need to get unique terms for these condition types
    if (!CONDITION_TYPES_USING_TERMS.includes(sessionAttribute._type)) {
      return;
    }

    let propertyToGetTermsOn = propertyName;

    if (propertyName && sessionAttribute._type === CONDITION_TYPES.PROFILE) {
      const matchingProperty = availableProfileProperties.find(
        a => a.field === propertyName
      );
      //don't try to get unique terms for non keyword fields
      if (!matchingProperty || !matchingProperty.isKeyword) {
        setSuggestions([]);
        return;
      }
      propertyToGetTermsOn = matchingProperty.cdsField;
    }

    if (propertyName) {
      getUniqueTerms(
        sessionAttribute._type,
        propertyToGetTermsOn,
        setSuggestions,
        onError,
        suggestionSearch,
        propertyValues
      );
    }
  }, [
    availableProfileProperties,
    sessionAttribute._type,
    propertyName,
    propertyValues,
    suggestionSearch
  ]);

  useEffect(() => {
    setShowPropertyValue(comparisonOperator !== EXISTS);
  }, [comparisonOperator]);

  return (
    <Box
      direction='row'
      fill='horizontal'
      flex={{ shrink: 0 }}
      background={background}
      pad='xsmall'
      gap='xsmall'
    >
      {sessionAttribute._type !== CONDITION_TYPES.AUDIENCE && (
        <Box justify='center' align='center'>
          <Button
            plain
            disabled={!canEdit}
            onClick={() => {
              onChange({ inverse: !inverse });
            }}
            title='Exclude users that meet this condition'
          >
            <Clear color={inverse ? 'red' : 'light-6'} />
          </Button>
        </Box>
      )}
      <Box
        background='#fff'
        flex={showPropertyValue ? { shrink: 0 } : { grow: 1 }}
        style={{ width }}
        alignSelf='start'
      >
        {
          {
            [CONDITION_TYPES.PROFILE]: (
              <ProfileFieldSelect
                availableProfileProperties={availableProfileProperties}
                value={propertyName}
                onChange={onChange}
                onError={onError}
                canEdit={canEdit}
              />
            ),
            [CONDITION_TYPES.PARTNER]: (
              <PartnerFieldSelect
                availablePartnerProperties={availablePartnerProperties}
                value={propertyName}
                onChange={onChange}
                canEdit={canEdit}
              />
            ),
            [CONDITION_TYPES.DEVICE]: (
              <DeviceFieldSelect
                value={propertyName}
                onChange={onChange}
                canEdit={canEdit}
              />
            ),
            [CONDITION_TYPES.QUERY_PARAM]: (
              <QueryFieldInput
                availableQueryParams={availableQueryParams}
                value={propertyName}
                onChange={onChange}
                canEdit={canEdit}
              />
            ),
            [CONDITION_TYPES.PLACEMENT]: (
              <TextInput
                disabled={!canEdit}
                value={propertyName}
                onChange={e => {
                  onChange({ propertyName: e.target.value });
                }}
              />
            )
            // [CONDITION_TYPES.AUDIENCE]: (
            //   <AudienceSelector
            //     audienceIds={audienceIds}
            //     setAudienceIds={setAudienceIds}
            //     canEdit={canEdit}
            //   />
            // )
          }[sessionAttribute._type]
        }
      </Box>
      {sessionAttribute._type !== CONDITION_TYPES.AUDIENCE && (
        <>
          <Box
            background='#fff'
            flex={showPropertyValue ? { shrink: 0 } : { grow: 1 }}
            style={{ width: '60px' }}
            alignSelf='start'
            pad={{ right: 'xsmall' }}
          >
            <FTSelect
              size='small'
              options={COMPARISON_OPERATORS}
              labelKey='label'
              valueKey='value'
              disabled={!canEdit}
              value={COMPARISON_OPERATORS.find(
                c => c.value === comparisonOperator
              )}
              onChange={optionValue => {
                const changes = {
                  comparisonOperator: optionValue.value
                };
                //changing from a multiple valued comparison to a single valued, remove all but hte first value
                if (comparisonOperator === 'In' && propertyValues.length > 1) {
                  changes.propertyValues = [propertyValues[0]];
                }
                if (optionValue.value === EXISTS) {
                  changes.propertyValues = [];
                }
                onChange(changes);
              }}
            />
          </Box>
          {showPropertyValue && (
            <Box background='#fff' flex={{ shrink: 0, grow: 1 }}>
              {comparisonOperator === 'In' ? (
                <TagSelector
                  tags={propertyValues}
                  onTagAdd={t => {
                    if (suggestionSearch !== '') {
                      setSuggestionSearch('');
                    }

                    onChange({ propertyValues: [...propertyValues, t] });
                  }}
                  onTagRemove={t => {
                    const newPropertyValues = [...propertyValues];
                    newPropertyValues.splice(newPropertyValues.indexOf(t), 1);
                    onChange({ propertyValues: newPropertyValues });
                  }}
                  suggestions={suggestions}
                  onTextChange={setSuggestionSearch}
                  placeholder='Add Value'
                  canEdit={canEdit}
                />
              ) : (
                <TextInput
                  disabled={!canEdit}
                  size='small'
                  value={propertyValues[0] || ''}
                  onChange={e => {
                    setSuggestionSearch(e.target.value);
                    onChange({ propertyValues: [e.target.value] });
                  }}
                  suggestions={suggestions}
                  onSelect={e => {
                    if (suggestionSearch !== '') {
                      setSuggestionSearch('');
                    }

                    onChange({ propertyValues: [e.suggestion] });
                  }}
                />
              )}
            </Box>
          )}
        </>
      )}

      <Button
        plain
        disabled={!canEdit}
        icon={<FormClose size='small' />}
        onClick={() => {
          if (sessionAttribute._type === CONDITION_TYPES.AUDIENCE) {
            setAudienceIds([]);
          }
          onRemove();
        }}
      />
    </Box>
  );
};

const scrollToEnd = element => {
  setTimeout(() => {
    element.scrollTo({
      top: element.scrollHeight,
      behavior: 'smooth'
    });
  }, 0);
};

export const getProfileProperties = (
  setProfileProperties,
  profileProperties,
  onError = () => {}
) => {
  searchItemsByType({ itemTypes: [USER_ATTRIBUTE], size: 1000 })
    .then(({ items }) => {
      const fields = items.map(f => {
        return {
          displayName: f.name,
          cdsField: `properties.attrs.${f.code}`,
          isKeyword: true,
          field: `profile.attrs.${f.code}`
        };
      });

      fields.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setProfileProperties([...profileProperties, ...fields]);
    })
    .catch(ex => {
      onError('Unable to get profile properties', JSON.stringify(ex));
    });
};

export const getPartnerProperties = (setPartnerProperties, onError) => {
  searchItemsByType({ itemTypes: [WEB_API_INTEGRATION], size: 1000 })
    .then(({ items }) => {
      const partnerItems = flatMap(
        items
          .filter(item => item.responseParameterMappings.length)
          .map(fItem => fItem.responseParameterMappings)
      )
        .filter(fl => fl.mapFrom && fl.mapTo)
        .map(pItem => {
          return {
            displayName: pItem.mapTo,
            isKeyword: true,
            field: `profile.partnerAttrs.${pItem.mapTo}`
          };
        });

      const filterDups = [
        ...new Map(partnerItems.map(item => [item['field'], item])).values()
      ];
      setPartnerProperties(filterDups);
    })
    .catch(ex => {
      onError('Unable to get partner properties', JSON.stringify(ex));
    });
};

const SessionAttributesSelector = ({
  sessionAttributes = [],
  setSessionAttributes,
  attributeMeet,
  setAttributeMeet,
  sessionAttributeConfig,
  hideZipCode,
  canEdit = true
}) => {
  const {
    conditionTypes = [],
    border = true,
    showLabel = true,
    height = 'small'
  } = sessionAttributeConfig;
  const audIds =
    sessionAttributes.find(sa => sa._type === CONDITION_TYPES.AUDIENCE)
      ?.propertyValues || [];
  const [audienceIds, setAudienceIds] = useState(audIds);
  const [isOpen, setIsOpen] = useState(false);
  const [profileProperties, setProfileProperties] = useState(
    hideZipCode
      ? PROFILE_FIELDS
      : [
          ...PROFILE_FIELDS,
          {
            displayName: 'Zip Code',
            cdsField: 'properties.zipCode',
            isKeyword: true,
            field: 'profile.zip'
          }
        ]
  );
  const [queryParams, setQueryParams] = useState([]);
  const [partnerProperties, setPartnerProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const overflowDiv = useRef();
  const dispatch = useDispatch();

  const onError = (primary, secondary) =>
    dispatch(addError(primary, secondary));
  //get profile properties when initializing
  useEffect(() => {
    getProfileProperties(setProfileProperties, profileProperties, onError);
    getPartnerProperties(setPartnerProperties, onError);
    getQueryParams(
      qps => {
        setQueryParams(
          qps.map(qp => {
            const qpParts = qp.split('.');
            return {
              field: qp,
              displayName: qpParts[qpParts.length - 1]
            };
          })
        );
      },
      false,
      false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      profileProperties.length > 0 &&
      queryParams.length > 0 &&
      partnerProperties.length > 0
    ) {
      setIsLoading(false);
    }
  }, [profileProperties, queryParams, partnerProperties]);

  useEffect(() => {
    const newSessionAttributes = sessionAttributes.map(sa => {
      if (sa._type === CONDITION_TYPES.AUDIENCE) {
        sa.propertyValues = audienceIds;
      }
      return sa;
    });

    setSessionAttributes(newSessionAttributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceIds]);

  return (
    <Box style={{ minWidth: '430px' }} gap='small'>
      <Box
        direction='row'
        fill='horizontal'
        justify={'between'}
        align='center'
        flex={{ shrink: 0 }}
      >
        {showLabel && (
          <Box flex={{ shrink: 0 }}>
            <Text>Session Attributes</Text>
          </Box>
        )}
        <Box
          direction='row'
          justify={showLabel ? 'end' : 'between'}
          gap='small'
          fill={'horizontal'}
        >
          {conditionTypes.length > 1 ? (
            <DropButton
              label='New'
              disabled={isLoading || !canEdit}
              open={isOpen}
              onClick={() => setIsOpen(!isOpen)}
              dropProps={{ onClickOutside: () => setIsOpen(false) }}
              dropAlign={{ top: 'bottom', left: 'left' }}
              dropContent={
                <Box>
                  {conditionTypes.includes(CONDITION_TYPES.DEVICE) && (
                    <Button
                      plain
                      hoverIndicator={true}
                      onClick={() => {
                        const newSessionAttributes = [...sessionAttributes];
                        newSessionAttributes.push(
                          getNewCondition(CONDITION_TYPES.DEVICE)
                        );
                        setSessionAttributes(newSessionAttributes);
                        setIsOpen(false);
                        scrollToEnd(overflowDiv.current);
                      }}
                    >
                      <Box width='small' pad='small'>
                        <Text>Device Rule</Text>
                      </Box>
                    </Button>
                  )}
                  {conditionTypes.includes(CONDITION_TYPES.QUERY_PARAM) && (
                    <Button
                      plain
                      hoverIndicator={true}
                      onClick={() => {
                        const newSessionAttributes = [...sessionAttributes];
                        newSessionAttributes.push(
                          getNewCondition(CONDITION_TYPES.QUERY_PARAM)
                        );
                        setSessionAttributes(newSessionAttributes);
                        setIsOpen(false);
                        scrollToEnd(overflowDiv.current);
                      }}
                    >
                      <Box width='small' pad='small'>
                        <Text>Query Param Rule</Text>
                      </Box>
                    </Button>
                  )}
                  {conditionTypes.includes(CONDITION_TYPES.PLACEMENT) && (
                    <Button
                      plain
                      hoverIndicator={true}
                      onClick={() => {
                        const newSessionAttributes = [...sessionAttributes];
                        newSessionAttributes.push(
                          getNewCondition(CONDITION_TYPES.PLACEMENT)
                        );
                        setSessionAttributes(newSessionAttributes);
                        setIsOpen(false);
                        scrollToEnd(overflowDiv.current);
                      }}
                    >
                      <Box width='small' pad='small'>
                        <Text>Placement Rule</Text>
                      </Box>
                    </Button>
                  )}
                  {conditionTypes.includes(CONDITION_TYPES.PROFILE) && (
                    <Button
                      plain
                      hoverIndicator={true}
                      onClick={() => {
                        const newSessionAttributes = [...sessionAttributes];
                        newSessionAttributes.push(
                          getNewCondition(CONDITION_TYPES.PROFILE)
                        );
                        setSessionAttributes(newSessionAttributes);
                        setIsOpen(false);
                        scrollToEnd(overflowDiv.current);
                      }}
                    >
                      <Box width='small' pad='small'>
                        <Text>Profile Rule</Text>
                      </Box>
                    </Button>
                  )}
                  {conditionTypes.includes(CONDITION_TYPES.AUDIENCE) && (
                    <Button
                      plain
                      disabled={
                        !!sessionAttributes.find(
                          a => a._type === CONDITION_TYPES.AUDIENCE
                        )
                      }
                      hoverIndicator={true}
                      onClick={() => {
                        const newSessionAttributes = [...sessionAttributes];
                        newSessionAttributes.push(
                          getNewCondition(
                            CONDITION_TYPES.AUDIENCE,
                            ATTR_PROPERTY_NAMES.USER_SEGMENTS,
                            'Contains'
                          )
                        );
                        setSessionAttributes(newSessionAttributes);
                        setIsOpen(false);
                        scrollToEnd(overflowDiv.current);
                      }}
                    >
                      <Box width='small' pad='small'>
                        <Text>Audience Rule</Text>
                      </Box>
                    </Button>
                  )}
                  {conditionTypes.includes(CONDITION_TYPES.PARTNER) && (
                    <Button
                      plain
                      hoverIndicator={true}
                      onClick={() => {
                        const newSessionAttributes = [...sessionAttributes];
                        newSessionAttributes.push(
                          getNewCondition(CONDITION_TYPES.PARTNER)
                        );
                        setSessionAttributes(newSessionAttributes);
                        setIsOpen(false);
                        scrollToEnd(overflowDiv.current);
                      }}
                    >
                      <Box width='small' pad='small'>
                        <Text>Partner Rule</Text>
                      </Box>
                    </Button>
                  )}
                </Box>
              }
            />
          ) : (
            <Button
              label='Add'
              disabled={!canEdit}
              onClick={() => {
                const newSessionAttributes = [...sessionAttributes];
                newSessionAttributes.push(getNewCondition(conditionTypes[0]));
                setSessionAttributes(newSessionAttributes);
                scrollToEnd(overflowDiv.current);
              }}
            ></Button>
          )}
          <Meet
            value={attributeMeet}
            setSelectedValue={setAttributeMeet}
            canEdit={canEdit}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box height={height} fill='horizontal' align='center' justify='center'>
          <Loader />
        </Box>
      ) : (
        <>
          <Box
            alignSelf='end'
            style={{
              fontStyle: 'italic'
            }}
            pad='xsmall'
          >
            <MeetDescription value={attributeMeet} />
          </Box>
          <Box
            height={height}
            fill='horizontal'
            flex={{ shrink: 0 }}
            border={border ? { side: 'all', color: 'border400' } : false}
            round='xsmall'
            overflow={{ vertical: 'auto' }}
            pad='xsmall'
            ref={overflowDiv}
          >
            {sessionAttributes
              .filter(sa => sa._type !== CONDITION_TYPES.INFERRED_ATTRIBUTES)
              .map((s, i) => {
                return (
                  <SessionAttribute
                    sessionAttribute={s}
                    onChange={newProperties => {
                      const newSessionAttributes = [...sessionAttributes];
                      newSessionAttributes.splice(
                        newSessionAttributes.indexOf(s),
                        1,
                        Object.assign({}, s, newProperties)
                      );
                      setSessionAttributes(newSessionAttributes);
                    }}
                    onRemove={() => {
                      const newSessionAttributes = [...sessionAttributes];
                      newSessionAttributes.splice(
                        newSessionAttributes.indexOf(s),
                        1
                      );
                      setSessionAttributes(newSessionAttributes);
                    }}
                    key={s._id}
                    background={i % 2 === 0 ? 'light-1' : 'light-3'}
                    availableProfileProperties={profileProperties}
                    availablePartnerProperties={partnerProperties}
                    availableQueryParams={queryParams}
                    audienceIds={audienceIds}
                    setAudienceIds={setAudienceIds}
                    canEdit={canEdit}
                  />
                );
              })}
          </Box>
        </>
      )}
    </Box>
  );
};

SessionAttributesSelector.displayName = 'SessionAttributesSelector';

export default SessionAttributesSelector;
